import { MessageSquare } from "lucide-react"

import { StepHeader, WizardLayout } from "@/components/agents/setup/shell"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

import type { BubblePosition, ChatbotSetupStepProps } from "./types"

export const PRESET_COLORS = [
  "#2563EB",
  "#7C3AED",
  "#DB2777",
  "#DC2626",
  "#D97706",
  "#16A34A",
  "#0891B2",
  "#374151",
]

function ColorSwatch({
  color,
  selected,
  onClick,
}: {
  color: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "size-8 rounded-full border-2 transition-transform",
        selected ? "border-foreground scale-110" : "border-transparent",
      )}
      style={{ backgroundColor: color }}
      aria-label={color}
    />
  )
}

export function PositionOption({
  value,
  label,
  selected,
  onClick,
}: {
  value: BubblePosition
  label: string
  selected: boolean
  onClick: () => void
}) {
  const isRight = value === "bottom-right"
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-col gap-3 rounded-lg border p-4 text-left transition-colors",
        selected
          ? "border-blue-300 bg-gradient-to-b from-blue-50 to-white dark:from-blue-950/60 dark:to-blue-900/20"
          : "border-border hover:bg-muted/30",
      )}
    >
      <div
        className={cn(
          "relative h-16 w-full overflow-hidden rounded-md",
          selected ? "bg-blue-100 dark:bg-blue-950/40" : "bg-muted",
        )}
      >
        <div
          className={cn(
            "absolute bottom-2 flex size-7 items-center justify-center rounded-full text-white",
            isRight ? "right-2" : "left-2",
          )}
          style={{ backgroundColor: selected ? "#2563EB" : "#9CA3AF" }}
        >
          <MessageSquare className="size-3.5" />
        </div>
      </div>
      <p
        className={cn(
          "text-sm leading-tight font-semibold",
          selected && "text-blue-600 dark:text-blue-400",
        )}
      >
        {label}
      </p>
    </button>
  )
}

export function AppearanceStep({
  stepNumber,
  totalSteps,
  data,
  onUpdate,
  onNext,
  nextLabel = "Continue",
  processing,
  businessName,
}: ChatbotSetupStepProps & { businessName: string }) {
  return (
    <WizardLayout
      footer={
        <Button onClick={onNext} disabled={!data.bot_name.trim() || processing}>
          {nextLabel}
        </Button>
      }
    >
      <StepHeader
        eyebrow={`Step ${stepNumber} of ${totalSteps}`}
        title="Appearance"
        description="Choose how the chat widget looks on your website."
      />

      <div className="space-y-3">
        <Label>Display name</Label>
        <Input
          value={data.bot_name}
          onChange={(e) => onUpdate({ bot_name: e.target.value })}
          placeholder={businessName || "Your business"}
        />
        <p className="text-muted-foreground text-xs">
          The title of the chat window. Usually your business name.
        </p>
      </div>

      <div className="space-y-3">
        <Label>Primary color</Label>
        <div className="flex flex-wrap items-center gap-2">
          {PRESET_COLORS.map((color) => (
            <ColorSwatch
              key={color}
              color={color}
              selected={data.primary_color === color}
              onClick={() => onUpdate({ primary_color: color })}
            />
          ))}
          <label className="relative">
            <input
              type="color"
              value={data.primary_color}
              onChange={(e) => onUpdate({ primary_color: e.target.value })}
              className="absolute inset-0 cursor-pointer opacity-0"
            />
            <span className="border-border text-muted-foreground hover:bg-muted/30 flex size-8 items-center justify-center rounded-full border text-xs transition-colors">
              +
            </span>
          </label>
        </div>
      </div>

      <div className="space-y-3">
        <Label>Bubble position</Label>
        <div className="grid grid-cols-2 gap-3">
          <PositionOption
            value="bottom-right"
            label="Bottom right"
            selected={data.bubble_position === "bottom-right"}
            onClick={() => onUpdate({ bubble_position: "bottom-right" })}
          />
          <PositionOption
            value="bottom-left"
            label="Bottom left"
            selected={data.bubble_position === "bottom-left"}
            onClick={() => onUpdate({ bubble_position: "bottom-left" })}
          />
        </div>
      </div>
    </WizardLayout>
  )
}
